import { useState } from "react";

/**
 * @typedef {Object} DateRangeInput
 * @property {string|null} from - The start date of the range as a string.
 * @property {string|null} to - The end date of the range as a string.
 */

/**
 * Props for the DateRangePicker component.
 * @typedef {Object} BasicRangeShortcutsProps
 * @property {DateRangeInput} value - The current date range.
 * @property {(newValue: DateRangeInput) => void} onChange - Callback to handle date range changes.
 */

/**
 * A simple date range picker component using Tailwind CSS with a dropdown for options.
 * @param {BasicRangeShortcutsProps} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */
const DateRangePicker = ({ value, onChange }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleChange = (field) => (e) => {
    onChange({ ...value, [field]: e.target.value });
  };

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  const setDateRange = (from, to) => {
    onChange({ from, to });
    setIsDropdownOpen(false);
  };

  const getDateRange = (offsetDays = 0, rangeType) => {
    const today = new Date();
    const start = new Date(today);

    if (rangeType === "week") {
      start.setDate(
        today.getDate() +
          offsetDays +
          (rangeType === "week" ? -today.getDay() : 0),
      );
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return [
        start.toISOString().split("T")[0],
        end.toISOString().split("T")[0],
      ];
    } else if (rangeType === "month") {
      start.setFullYear(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return [
        start.toISOString().split("T")[0],
        end.toISOString().split("T")[0],
      ];
    }
    return [null, null]; // Fallback if rangeType is not recognized
  };

  return (
    <div className="flex gap-4">
      <div className="flex space-x-2">
        <input
          type="date"
          value={value.from || ""}
          onChange={handleChange("from")}
          className="border border-gray-300 rounded p-2"
          placeholder="From"
        />
        <input
          type="date"
          value={value.to || ""}
          onChange={handleChange("to")}
          className="border border-gray-300 rounded p-2"
          placeholder="To"
        />
      </div>
      <div className="relative">
        <button
          onClick={toggleDropdown}
          className="flex items-center bg-blue-500 text-white rounded p-2"
        >
          Options
          <span className="ml-2">&#9662;</span>
        </button>
        {isDropdownOpen && (
          <div className="absolute z-10 mt-2 w-full bg-white border border-gray-300 rounded shadow-lg">
            <div className="flex flex-col gap-3">
              <button
                onClick={() =>
                  setDateRange(
                    new Date().toISOString().split("T")[0],
                    new Date().toISOString().split("T")[0],
                  )
                }
                className="p-2 text-left"
              >
                Today
              </button>
              <button
                onClick={() => setDateRange(...getDateRange(0, "week"))}
                className="p-2 text-left"
              >
                This Week
              </button>
              <button
                onClick={() => setDateRange(...getDateRange(-7, "week"))}
                className="p-2 text-left"
              >
                Last Week
              </button>
              <button
                onClick={() => setDateRange(...getDateRange(0, "month"))}
                className="p-2 text-left"
              >
                Current Month
              </button>
              {/* <button
                onClick={() => setDateRange(null, null)}
                className="p-2 text-left"
              >
                Reset
              </button> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangePicker;
